import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { getBadgeDefinition, getBadgeRarityColor } from "@/types/badges";
import confetti from "canvas-confetti";

interface BadgeEarnedAnimationProps {
  badgeSlug: string;
  isVisible: boolean;
  onComplete: () => void;
}

interface QuestCompletedAnimationProps {
  questTitle: string;
  xpAwarded: number;
  isVisible: boolean;
  onComplete: () => void;
}

interface LevelUpAnimationProps {
  newLevel: number;
  isVisible: boolean;
  onComplete: () => void;
}

export const BadgeEarnedAnimation = ({ badgeSlug, isVisible, onComplete }: BadgeEarnedAnimationProps) => {
  const badge = getBadgeDefinition(badgeSlug);

  useEffect(() => {
    if (isVisible && badge) {
      // Trigger confetti
      confetti({
        particleCount: 100,
        spread: 70,
        origin: { y: 0.6 },
        colors: ['#FFD700', '#FFA500', '#FF6347']
      });

      // Auto-complete after animation
      const timer = setTimeout(onComplete, 3000);
      return () => clearTimeout(timer);
    }
  }, [isVisible, badge, onComplete]);

  if (!badge) return null;

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0, scale: 0.5, y: 50 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.5, y: -50 }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm"
          onClick={onComplete}
        >
          <motion.div
            initial={{ rotateY: -90 }}
            animate={{ rotateY: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className={`bg-gradient-to-br ${getBadgeRarityColor(badge.rarity)} p-8 rounded-2xl shadow-2xl max-w-md mx-4 text-center`}
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.5, delay: 0.4, type: "spring", bounce: 0.6 }}
              className="text-6xl mb-4"
            >
              {badge.icon}
            </motion.div>
            
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.6 }}
              className="text-2xl font-bold text-white mb-2"
            >
              Badge Earned!
            </motion.h2>
            
            <motion.h3
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.7 }}
              className="text-xl font-semibold text-white mb-2"
            >
              {badge.name}
            </motion.h3>
            
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.8 }}
              className="text-white/90 mb-4"
            >
              {badge.description}
            </motion.p>
            
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3, delay: 1 }}
              className="text-sm text-white/70"
            >
              Tap to continue
            </motion.div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export const QuestCompletedAnimation = ({ questTitle, xpAwarded, isVisible, onComplete }: QuestCompletedAnimationProps) => {
  useEffect(() => {
    if (isVisible) {
      // Trigger quest completion confetti
      confetti({
        particleCount: 80,
        spread: 60,
        origin: { y: 0.7 },
        colors: ['#00FF00', '#32CD32', '#7CFC00']
      });

      const timer = setTimeout(onComplete, 2500);
      return () => clearTimeout(timer);
    }
  }, [isVisible, onComplete]);

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0.8 }}
          transition={{ duration: 0.5 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm"
          onClick={onComplete}
        >
          <motion.div
            initial={{ y: 100 }}
            animate={{ y: 0 }}
            transition={{ duration: 0.6, type: "spring", bounce: 0.4 }}
            className="bg-gradient-to-br from-green-500 to-emerald-600 p-6 rounded-xl shadow-xl max-w-sm mx-4 text-center"
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ duration: 0.4, delay: 0.2, type: "spring", bounce: 0.6 }}
              className="text-4xl mb-3"
            >
              ✅
            </motion.div>
            
            <motion.h2
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.3 }}
              className="text-lg font-bold text-white mb-1"
            >
              Quest Completed!
            </motion.h2>
            
            <motion.p
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.4 }}
              className="text-white/90 text-sm mb-2"
            >
              {questTitle}
            </motion.p>
            
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4, delay: 0.5 }}
              className="bg-white/20 rounded-lg px-3 py-1"
            >
              <span className="text-white font-semibold">+{xpAwarded} XP</span>
            </motion.div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export const LevelUpAnimation = ({ newLevel, isVisible, onComplete }: LevelUpAnimationProps) => {
  useEffect(() => {
    if (isVisible) {
      // Epic level up confetti
      const duration = 3000;
      const animationEnd = Date.now() + duration;

      const randomInRange = (min: number, max: number) => {
        return Math.random() * (max - min) + min;
      };

      const frame = () => {
        const timeLeft = animationEnd - Date.now();

        if (timeLeft <= 0) {
          return;
        }

        const particleCount = 50 * (timeLeft / duration);

        confetti({
          particleCount,
          startVelocity: 30,
          spread: 360,
          origin: {
            x: randomInRange(0.1, 0.3),
            y: Math.random() - 0.2
          },
          colors: ['#FFD700', '#FF6347', '#FF69B4', '#00FF00', '#1E90FF']
        });
        confetti({
          particleCount,
          startVelocity: 30,
          spread: 360,
          origin: {
            x: randomInRange(0.7, 0.9),
            y: Math.random() - 0.2
          },
          colors: ['#FFD700', '#FF6347', '#FF69B4', '#00FF00', '#1E90FF']
        });

        requestAnimationFrame(frame);
      };

      frame();

      const timer = setTimeout(onComplete, 4000);
      return () => clearTimeout(timer);
    }
  }, [isVisible, onComplete]);

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.5 }}
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm"
          onClick={onComplete}
        >
          <motion.div
            initial={{ scale: 0.5, rotateY: -180 }}
            animate={{ scale: 1, rotateY: 0 }}
            transition={{ duration: 1, type: "spring", bounce: 0.5 }}
            className="bg-gradient-to-br from-yellow-400 via-orange-500 to-red-500 p-8 rounded-3xl shadow-2xl max-w-lg mx-4 text-center"
          >
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: [0, 1.2, 1] }}
              transition={{ duration: 1, delay: 0.5, times: [0, 0.6, 1] }}
              className="text-8xl mb-4"
            >
              🎉
            </motion.div>
            
            <motion.h1
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.8 }}
              className="text-4xl font-bold text-white mb-2"
            >
              LEVEL UP!
            </motion.h1>
            
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 1 }}
              className="text-6xl font-bold text-white mb-4"
            >
              {newLevel}
            </motion.div>
            
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 1.2 }}
              className="text-xl text-white/90 mb-6"
            >
              Congratulations on reaching level {newLevel}!
            </motion.p>
            
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3, delay: 1.5 }}
              className="text-sm text-white/70"
            >
              Tap to continue
            </motion.div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};